import { useMemo } from 'react';
import { useUser } from './useUser';

interface Permission {
  id: number;
  name: string;
  url: string;
  enabled: boolean;
  isAdminDisabled?: boolean;
}

interface SubModule {
  id: number;
  name: string;
  url: string;
  enabled: boolean;
}

interface Module {
  id: number;
  name: string;
  url: string;
  enabled: boolean;
  isAdminDisabled?: boolean;
  permissions: Permission[];
  subModules: SubModule[];
}

interface User {
  id?: string;
  samId?: string;
  email: string;
  accountType?: string;
  role?: string;
  modules: Module[];
  username?: string;
  status?: string;
  designatedOperator?: string;
}

interface PermissionCheck {
  hasPermission: boolean;
  isLoading: boolean;
}

/**
 * Hook to check if the current user has a specific permission
 * 
 * This function checks for permissions in two ways:
 * 1. By matching the module URL + permission URL
 * 2. By matching the module name and permission name for backward compatibility
 * 
 * @param modulePath - The URL path of the module (e.g., '/eno1')
 * @param permissionPath - The URL path of the permission (e.g., '/upload')
 * @returns Object with hasPermission flag and loading state
 */
export function usePermission(modulePath: string, permissionPath: string): PermissionCheck {
  const { data: userInfo, isLoading } = useUser();
  
  return useMemo(() => {
    if (isLoading || !userInfo) {
      return { hasPermission: false, isLoading: true };
    }
    
    const user = userInfo as User;
    
    if (!user.modules || !Array.isArray(user.modules)) {
      return { hasPermission: false, isLoading: false };
    }
    
    // First, try to match by URLs
    // Check if the requested URL matches any enabled permission in any enabled module
    const fullPath = `${modulePath}${permissionPath}`;

    // Check through modules
    for (const module of user.modules) {
      if (!module.enabled || module.isAdminDisabled) {
        continue;
      }
      
      // Check direct permissions within the module
      const hasDirectPermission = module.permissions?.some(permission => 
        permission.enabled && 
        !permission.isAdminDisabled && 
        permission.url === fullPath
      );
      
      if (hasDirectPermission) {
        return { hasPermission: true, isLoading: false };
      }
      
      // Also check if the moduleUrl matches and there's a permission for the specific path
      if (module.url === modulePath) {
        const hasSpecificPermission = module.permissions?.some(permission => 
          permission.enabled && 
          !permission.isAdminDisabled && 
          permission.url === fullPath
        );
        
        if (hasSpecificPermission) {
          return { hasPermission: true, isLoading: false };
        }
      }

      // Check subModules URLs (for cases where modulePath directly matches a subModule URL)
      if (module.subModules && Array.isArray(module.subModules)) {
        const hasSubModuleAccess = module.subModules.some(subModule => 
          subModule.enabled && 
          subModule.url === modulePath
        );
        
        if (hasSubModuleAccess) {
          return { hasPermission: true, isLoading: false };
        }
        
        // Also check if there's a subModule that matches the full path
        const hasSubModuleFullPath = module.subModules.some(subModule => 
          subModule.enabled && 
          subModule.url === fullPath
        );
        
        if (hasSubModuleFullPath) {
          return { hasPermission: true, isLoading: false };
        }
      }
    }
    
    // Fallback to checking by module name and permission name (for backward compatibility)
    const foundModule = user.modules.find(
      mod => mod.name === modulePath && mod.enabled && !mod.isAdminDisabled
    );
    
    if (foundModule) {
      const foundPermission = foundModule.permissions.find(
        perm => perm.name === permissionPath && perm.enabled && !perm.isAdminDisabled
      );
      
      if (foundPermission) {
        return { hasPermission: true, isLoading: false };
      }
    }
    
    return { hasPermission: false, isLoading: false };
  }, [userInfo, isLoading, modulePath, permissionPath]);
} 